import isEqual from 'lodash/isEqual.js'

import {
  getState,
  setForm,
  updateForm,
  removeForm,
  formsSelector,
  formSelector,
} from '../../../../store.js'
import {DEFAULT_DATE_RANGE_TYPE} from '../../../../common/constants/OrderListDateRangeFilters.js'
import {TAG_FILTER_OR} from '../../../../common/constants/Tags.js'
import {clearFilterPanelSearch} from './FilterPanel.js'
import {clearTagFilterPanelSearch} from './TagFilterPanel.js'
import {
  cleanOrderQuery,
  isUsingOldParamSelector,
  orderListQueryToOrderQuery,
  summarizeOrderDateRangeParams,
} from '../../orderListSelectors.js'
import {
  navigateOrderList,
  updateAllocationFilters,
  updateCarrierError,
  updateCartFilters,
  updateShipperFilters,
  updateDateRangeFilters,
  updateHasRevision,
  updateHasSimilarOpenAddresses,
  updateInBatch,
  updateOrderExcludeTags,
  updateOrderListStatus,
  updateOrderTagFilterBy,
  updateOrderTagFilters,
  updateOrderUntaggedFilter,
  updateSearchText,
  updateShipToCountry,
  updateShipToIsBillTo,
  updateShipToStatus,
  updateSupplierFilters,
  updateWarehouseFilters,
  updateOrderNumberFilters,
  updateSKUFilters,
  updateLabelPrinted,
  updatePackingListPrinted,
} from '../../orderListActions.js'
import deferPromise from '../../../../common/deferPromise.js'
import {
  REPORT_INTERVALS,
  REPORT_INTERVAL_MONTH,
} from '../../../../common/constants/Reports.js'
import {
  isNonZeroPositiveInteger,
  isNonZeroPositiveNumeric,
  isPresent,
} from '../../../../common/utils.js'
import {ensureArray, ensureTroolean} from '../../../../common/ensure.js'
import {getTimeZone} from '../../../../common/date.js'
import {
  filterConfigNameSelector,
  filterConfigSelector,
  filterConfigsSortedByIDSelector,
  NEW_FILTER_CONFIG_ID,
  saveFilterConfig,
} from '../../../../data/filterConfigs.js'
import {orderTagsSelector} from '../../../../data/orderTags.js'
import {showConfirmMessageModal} from '../../../../common/components/Modal/ConfirmMessageModal.js'
import TextInput from '../../../../common/components/TextInput.js'

const MODAL_FORM = 'order_list_filter_modal'

export const STATUS_FILTER_PANEL = 'status'
export const ALLOCATION_FILTER_PANEL = 'allocation'
export const SALES_CHANNEL_FILTER_PANEL = 'sales_channel'
export const SHIPPER_FILTER_PANEL = 'shipper'
export const SUPPLIER_FILTER_PANEL = 'supplier'
export const WAREHOUSE_FILTER_PANEL = 'warehouse'
export const TAG_FILTER = 'tag'
export const DATE_RANGE_FILTER_PANEL = 'date_range'
export const ORDER_NUMBER_FILTER_PANEL = 'order_number'
export const SKU_FILTER_PANEL = 'sku'
export const MISC_FILTER_PANEL = 'misc'

export const ORDER_LIST_FILTER_OPTIONS = [
  {value: STATUS_FILTER_PANEL, display: 'Status'},
  {value: ALLOCATION_FILTER_PANEL, display: 'Allocation'},
  {value: SALES_CHANNEL_FILTER_PANEL, display: 'Sales Channels'},
  {value: SUPPLIER_FILTER_PANEL, display: 'Suppliers'},
  {value: WAREHOUSE_FILTER_PANEL, display: 'Warehouses'},
  {value: TAG_FILTER, display: 'Tags'},
  {value: DATE_RANGE_FILTER_PANEL, display: 'Date Range'},
  {value: ORDER_NUMBER_FILTER_PANEL, display: 'Order Numbers'},
  {value: SKU_FILTER_PANEL, display: 'SKUs'},
  {value: MISC_FILTER_PANEL, display: 'Misc'},
  {value: SHIPPER_FILTER_PANEL, display: 'Carrier'},
]

export async function showOrderListFilterModal({query = {}} = {}) {
  const {updates} = await showOrderListFilterModalPrime({
    query,
    singleRun: true,
  })

  return await navigateOrderList(updates)
}

function getAPIQuery(originalForm, updates) {
  const orderTags = orderTagsSelector(getState())
  const orderListQuery = {...originalForm, ...updates}

  const orderQuery = cleanOrderQuery(
    orderListQueryToOrderQuery(orderListQuery, orderTags),
  )

  delete orderQuery.sort
  delete orderQuery.limit
  delete orderQuery.offset

  if (!orderListQuery.includeTimezone) {
    delete orderQuery.timezone
  }

  return orderQuery
}

export async function showOrderQueryFilterModal({
  query = {},
  singleRun = true,
  type,
} = {}) {
  const {originalForm, updates} = await showOrderListFilterModalPrime({
    query,
    singleRun,
    type,
  })

  if (!updates) {
    return query
  }

  return getAPIQuery(originalForm, updates)
}

export async function showOrderListFilterConfigModal({
  query = {},
  filterConfigID,
}) {
  if (filterConfigID && filterConfigID !== NEW_FILTER_CONFIG_ID) {
    const filterConfig = filterConfigSelector(getState(), {filterConfigID})

    query = filterConfig.filter
  }

  const {originalForm, updates, filterConfigName} =
    await showOrderListFilterModalPrime({
      query,
      filterConfigID,
      singleRun: true,
    })

  if (!updates || !filterConfigName) {
    return
  }

  await saveFilterConfig(filterConfigID, {
    name: filterConfigName.trim(),
    type: 'order',
    filter: getAPIQuery(originalForm, updates),
  })
}

export async function showOrderListFilterModalPrime({
  query = {},
  filterConfigID,
  filterConfigName,
  singleRun = true,
  deferredPromise = deferPromise(),
  originalForm,
  type,
} = {}) {
  const {
    startDate,
    endDate,
    dateFilterType,
    intervalType,
    intervalAmount,
    intervalTrunc,
  } = summarizeOrderDateRangeParams(query)

  const form = {
    searchText: query.search || '',
    status: query.status || [],
    allocationFilters: query.allocation_status || [],
    cartFilters: query.sales_channel || [],
    shipper: query.shipper || [],
    supplierFilters: query.supplier || [],
    warehouseFilters: query.warehouse_id || [],
    dateFilterType: dateFilterType || DEFAULT_DATE_RANGE_TYPE,
    startDate: startDate || null,
    endDate: endDate || null,
    intervalType: intervalType || (!singleRun ? REPORT_INTERVALS[0] : ''),
    intervalAmount: intervalAmount || '1',
    intervalTrunc: intervalTrunc || false,
    tag: ensureArray(query.tag),
    orderUntaggedFilter: query.untagged || false,
    orderTagFilterOperator: query.tag_filter_by || TAG_FILTER_OR,
    exclude_tags: query.exclude_tags || [],
    label_printed: query.label_printed,
    packing_list_printed: query.packing_list_printed,
    hasSimilarOpenAddresses: query.has_similar_open_addresses || false,
    shipToIsBillTo: ensureTroolean(query.ship_to_is_bill_to),
    hasRevision: ensureTroolean(query.has_revision),
    shipToStatus: query.ship_to_status || '',
    shipToCountry: (query.ship_to_country && query.ship_to_country[0]) || '',
    carrier_error: ensureTroolean(query.carrier_error),
    inBatch: ensureTroolean(query.in_batch),
    order_number: ensureArray(query.order_number),
    sku: ensureArray(query.sku),
    timezone: getTimeZone(),
    includeTimezone: !!query.timezone,
  }

  setForm(MODAL_FORM, {
    type,
    singleRun,
    deferredPromise,
    originalForm: originalForm || {...form},
    filterConfigID,
    filterConfigName:
      filterConfigName !== undefined
        ? filterConfigName
        : filterConfigNameSelector(getState(), {filterConfigID}),
    ...form,
  })

  // timezone is sticky so override its sticky value applied in setForm()
  if (query.timezone) {
    updateModalForm({timezone: query.timezone, includeTimezone: true})
  }

  clearFilterPanelSearch(SALES_CHANNEL_FILTER_PANEL)
  clearFilterPanelSearch(SUPPLIER_FILTER_PANEL)
  clearFilterPanelSearch(WAREHOUSE_FILTER_PANEL)
  clearFilterPanelSearch(SHIPPER_FILTER_PANEL)
  clearTagFilterPanelSearch()

  return deferredPromise.promise
}

export function updateModalForm(...args) {
  updateForm(MODAL_FORM, ...args)
}

export function closeModal(updates) {
  const {originalForm, deferredPromise, filterConfigName} =
    modalFormSelector(getState())

  deferredPromise.resolve({originalForm, updates, filterConfigName})

  removeForm(MODAL_FORM)
}

export function clearFilters() {
  const {
    singleRun,
    deferredPromise,
    originalForm,
    filterConfigName,
    filterConfigID,
    type,
  } = modalFormSelector(getState())

  // re-setup modal form with most of the pieces reused
  // reuse deferredPromise so that any awaits from the original call to show... will resolve
  // reuse originalForm so applyFilter() has the correct thing to compare against
  // reuse singleRun because that is part of the context
  // reuse filterConfig* because we might be editing a filterConfig
  showOrderListFilterModalPrime({
    singleRun,
    deferredPromise,
    originalForm,
    filterConfigName,
    filterConfigID,
    type,
  })
}

export function modalFormSelector(state) {
  return formsSelector(state)[MODAL_FORM]
}

function filterConfigNameErrorSelector(
  state,
  {filterConfigName, filterConfigID},
) {
  const filterConfigs = filterConfigsSortedByIDSelector(state)
  const name = filterConfigName.trim()

  if (!isPresent(name)) {
    return 'Filter name is required'
  } else if (
    filterConfigs.find(
      (filterConfig) =>
        filterConfig.id !== filterConfigID && filterConfig.name === name,
    )
  ) {
    return 'Filter name must be unique'
  }
}

export function errorsSelector(state) {
  const {intervalType, intervalAmount, filterConfigID, filterConfigName} =
    modalFormSelector(state)
  const errors = {}

  if (intervalType) {
    if (
      intervalType === REPORT_INTERVAL_MONTH &&
      !isNonZeroPositiveInteger(intervalAmount)
    ) {
      errors.intervalAmount = 'Amount must be a positive whole number'
      errors.preventSave = true
    } else if (
      intervalType !== REPORT_INTERVAL_MONTH &&
      !isNonZeroPositiveNumeric(intervalAmount)
    ) {
      errors.intervalAmount = 'Amount must be a positive number'
      errors.preventSave = true
    }
  }

  if (filterConfigID) {
    const filterConfigNameError = filterConfigNameErrorSelector(state, {
      filterConfigName,
      filterConfigID,
    })

    if (filterConfigNameError) {
      errors.filterConfigName = filterConfigNameError
      errors.preventSave = true
    }
  }

  return errors
}

export function applyFilter() {
  const {
    originalForm,
    searchText,
    status,
    allocationFilters,
    cartFilters,
    shipper,
    supplierFilters,
    warehouseFilters,
    dateFilterType,
    startDate,
    endDate,
    intervalType,
    intervalAmount,
    intervalTrunc,
    tag,
    orderUntaggedFilter,
    orderTagFilterOperator,
    exclude_tags,
    label_printed,
    packing_list_printed,
    hasSimilarOpenAddresses,
    shipToIsBillTo,
    hasRevision,
    shipToStatus,
    shipToCountry,
    carrier_error,
    inBatch,
    order_number,
    sku,
    timezone,
    includeTimezone,
  } = modalFormSelector(getState())
  const isUsingOldParam = isUsingOldParamSelector(getState())

  let updates = {}

  if (searchText !== originalForm.search) {
    updates = {...updates, ...updateSearchText(searchText)}
  }

  if (!isEqual(status, originalForm.status)) {
    updates = {...updates, ...updateOrderListStatus(status)}
  }

  if (!isEqual(allocationFilters, originalForm.allocationFilters)) {
    updates = {...updates, ...updateAllocationFilters(allocationFilters)}
  }

  if (!isEqual(cartFilters, originalForm.cartFilters)) {
    updates = {...updates, ...updateCartFilters(cartFilters)}
  }

  if (!isEqual(shipper, originalForm.shipper)) {
    updates = {...updates, ...updateShipperFilters(shipper)}
  }

  if (!isEqual(supplierFilters, originalForm.supplierFilters)) {
    updates = {...updates, ...updateSupplierFilters(supplierFilters)}
  }

  if (!isEqual(warehouseFilters, originalForm.warehouseFilters)) {
    updates = {...updates, ...updateWarehouseFilters(warehouseFilters)}
  }

  if (
    dateFilterType !== originalForm.dateFilterType ||
    startDate !== originalForm.startDate ||
    endDate !== originalForm.endDate ||
    intervalType !== originalForm.intervalType ||
    intervalAmount !== originalForm.intervalAmount ||
    intervalTrunc !== originalForm.intervalTrunc ||
    timezone !== originalForm.timezone ||
    includeTimezone !== originalForm.includeTimezone
  ) {
    updates = {
      ...updates,
      ...updateDateRangeFilters(
        {
          dateFilterType,
          startDate,
          endDate,
          intervalType,
          intervalAmount,
          intervalTrunc,
          timezone: includeTimezone ? timezone : undefined,
        },
        updates.status || originalForm.status,
      ),
      includeTimezone,
    }
  }

  if (!isEqual(tag, originalForm.tag) || isUsingOldParam.tag) {
    updates = {...updates, ...updateOrderTagFilters(tag)}
  }

  if (orderUntaggedFilter !== originalForm.orderUntaggedFilter) {
    updates = {...updates, ...updateOrderUntaggedFilter(orderUntaggedFilter)}
  }

  if (orderTagFilterOperator !== originalForm.orderTagFilterOperator) {
    updates = {...updates, ...updateOrderTagFilterBy(orderTagFilterOperator)}
  }

  if (!isEqual(exclude_tags, originalForm.exclude_tags)) {
    updates = {...updates, ...updateOrderExcludeTags(exclude_tags)}
  }

  if (
    label_printed !== originalForm.label_printed ||
    isUsingOldParam.label_printed
  ) {
    updates = {...updates, ...updateLabelPrinted(label_printed)}
  }

  if (packing_list_printed !== originalForm.packing_list_printed) {
    updates = {...updates, ...updatePackingListPrinted(packing_list_printed)}
  }

  if (hasSimilarOpenAddresses !== originalForm.hasSimilarOpenAddresses) {
    updates = {
      ...updates,
      ...updateHasSimilarOpenAddresses(hasSimilarOpenAddresses),
    }
  }

  if (shipToIsBillTo !== originalForm.shipToIsBillTo) {
    updates = {...updates, ...updateShipToIsBillTo(shipToIsBillTo)}
  }

  if (hasRevision !== originalForm.hasRevision) {
    updates = {...updates, ...updateHasRevision(hasRevision)}
  }

  if (shipToStatus !== originalForm.shipToStatus) {
    updates = {...updates, ...updateShipToStatus(shipToStatus)}
  }

  if (shipToCountry !== originalForm.shipToCountry) {
    updates = {...updates, ...updateShipToCountry(shipToCountry)}
  }

  if (carrier_error !== originalForm.carrier_error) {
    updates = {...updates, ...updateCarrierError(carrier_error)}
  }

  if (inBatch !== originalForm.inBatch) {
    updates = {...updates, ...updateInBatch(inBatch)}
  }

  if (!isEqual(order_number, originalForm.order_number)) {
    updates = {
      ...updates,
      ...updateOrderNumberFilters(order_number),
    }
  }

  if (!isEqual(sku, originalForm.sku)) {
    updates = {
      ...updates,
      ...updateSKUFilters(sku),
    }
  }

  return updates
}

export async function showSaveFilterImpromptuModal() {
  const filterConfigID = NEW_FILTER_CONFIG_ID

  await showConfirmMessageModal({
    title: 'Save Filter Set',
    Message: ({form, errors, updateModalForm}) => (
      <>
        <p className="fs-01 lh-md margin-bottom-15">
          Would you like to save this filter set for later use?
        </p>
        <div className="w-65 margin-bottom-10">
          <TextInput
            id="filter_name"
            label="Filter Set Name"
            value={form.confirmValue}
            onChange={(name) => updateModalForm({confirmValue: name})}
            errorMessage={errors.filterConfigName}
          />
        </div>
      </>
    ),
    modalSize: 'sm',
    confirmText: 'Save and Apply Filter Set',
    cancelText: 'Cancel',
    confirmValue: filterConfigNameSelector(getState(), {filterConfigID}),
    errorsSelector(state, {formName}) {
      const {confirmValue: filterConfigName} = formSelector(state, {formName})
      const errors = {}

      const filterConfigNameError = filterConfigNameErrorSelector(state, {
        filterConfigName,
        filterConfigID,
      })

      if (filterConfigNameError) {
        errors.filterConfigName = filterConfigNameError
        errors.preventSave = true
      }

      return errors
    },
    async onResolve({outcome}) {
      if (!outcome) {
        return
      }

      const updates = applyFilter()
      const {originalForm} = modalFormSelector(getState())

      await saveFilterConfig(filterConfigID, {
        name: outcome.trim(),
        type: 'order',
        filter: getAPIQuery(originalForm, updates),
      })

      closeModal(updates)
    },
  })
}
